import { Link } from 'react-router-dom'
import { resourceLinks } from '../../../MarketingPages/links'
import { Icon } from '@iconify/react'
import {
  sorteeLandingFooter,
  type SorteeLandingFooterLink,
} from '../content/sorteeLandingContent'
import { ArrowRightIcon, LandingIcon } from './icons'
import styles from './LandingFooter.module.css'

function linkTargetProps(link: Pick<SorteeLandingFooterLink, 'openInNewTab'>) {
  if (!link.openInNewTab) {
    return {}
  }

  return {
    target: '_blank',
    rel: 'noopener noreferrer',
  } as const
}

function FooterNavList({
  heading,
  links,
}: {
  heading: string
  links: readonly SorteeLandingFooterLink[]
}) {
  return (
    <div className={styles.column}>
      <h3 className={styles.columnHeading}>{heading}</h3>
      <ul className={styles.navList}>
        {links.map((link) => (
          <li key={`${link.label}-${link.href}`}>
            <a href={link.href} className={styles.navLink} {...linkTargetProps(link)}>
              <span className={styles.navLinkIcon} aria-hidden="true">
                <ArrowRightIcon />
              </span>
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function LandingFooter() {
  return (
    <footer id="contato" className={styles.footer} role="contentinfo">
      <div className={styles.topGlow} aria-hidden="true" />
      <div className={`${styles.orb} ${styles.orbTop}`} aria-hidden="true" />
      <div className={`${styles.orb} ${styles.orbBottom}`} aria-hidden="true" />

      <div className={styles.container}>
        <a className={styles.footerBrand} href="#conteudo" aria-label="Voltar ao início">
          <img src="/logo/sortee.png" alt="Logo Sortee" width="2089" height="753" />
        </a>
        <div className={styles.grid}>
          <div className={styles.column}>
            <h3 className={`${styles.columnHeading} ${styles.contactHeading}`}>
              {sorteeLandingFooter.contactHeading}
            </h3>
            <p className={styles.description}>{sorteeLandingFooter.contactDescription}</p>

            <div className={styles.phoneRow}>
              <span className={styles.phoneIcon} aria-hidden="true">
                <Icon icon="mdi:whatsapp" />
              </span>
              <a
                href={sorteeLandingFooter.phone.href}
                {...linkTargetProps(sorteeLandingFooter.phone)}
              >
                {sorteeLandingFooter.phone.label}
              </a>
            </div>

            <div className={styles.emailRow}>
              <LandingIcon name="mail" />
              <a href={sorteeLandingFooter.email.href}>
                {sorteeLandingFooter.email.label}
              </a>
            </div>

            <div className={styles.socialBlock}>
              <h4 className={styles.socialHeading}>
                {sorteeLandingFooter.socialHeading}
              </h4>
              <div className={styles.socialLinks}>
                {sorteeLandingFooter.socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className={styles.socialLink}
                    {...linkTargetProps(link)}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
              <p className={styles.groupsText}>
                {sorteeLandingFooter.groupsPrefix}{' '}
                {sorteeLandingFooter.groups.map((group, index) => (
                  <span key={group.href}>
                    {index > 0 ? ` ${sorteeLandingFooter.groupsConjunction} ` : null}
                    <a
                      href={group.href}
                      className={styles.groupLink}
                      {...linkTargetProps(group)}
                    >
                      {group.label}
                    </a>
                  </span>
                ))}
              </p>
            </div>
          </div>

          <FooterNavList
            heading={sorteeLandingFooter.navHeading}
            links={sorteeLandingFooter.navLinks}
          />
          <FooterNavList
            heading={sorteeLandingFooter.solutionsHeading}
            links={sorteeLandingFooter.solutionsLinks}
          />
        </div>

        <nav className={styles.resourceNav} aria-label="Explore a plataforma">
          <h3 className={styles.columnHeading}>Explore a plataforma</h3>
          <div>
            <Link to="/recursos">Todos os recursos</Link>
            <Link to="/como-funciona">Guia para criar sua campanha</Link>
            {resourceLinks.map((resource) => (
              <Link key={resource.slug} to={`/recursos/${resource.slug}`}>
                {resource.title}
              </Link>
            ))}
          </div>
        </nav>
        <div className={styles.bottom}>
          <p className={styles.copyright}>{sorteeLandingFooter.copyright}</p>
          <p className={styles.rights}>{sorteeLandingFooter.rightsReserved}</p>
          <nav className={styles.legalLinks} aria-label="Documentos legais">
            {sorteeLandingFooter.legalLinks.map((link) => (
              <a key={link.href} href={link.href} className={styles.legalLink}>
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  )
}
